import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Button from '../../components/Button/Button';
import {
  buildFlightPassengers,
  createFlightOfferRequest,
  summarizeFlightOffer,
} from '../../lib/duffelFlightsApi';
import { buildLocationSearchIndex, searchLocationIndex } from '../../lib/locationSearchIndex';
import {
  defaultDisplayForCode,
  resolveIataFromCatalogCity,
  resolveIataFromFreeText,
} from '../../lib/flightPlaceIata';
import { fetchSearchCatalogFromNetwork, getSearchCatalogSync } from '../../services/g2TravelApi';
import './Flights.css';

const MIN_FLIGHT_PLACE_QUERY = 3;

const buildFlightCityDisplayLabel = (suggestion) => {
  if (!suggestion) return '';
  const name = (suggestion.name || '').trim();
  const co = (suggestion.country || '').trim();
  return co ? `${name}, ${co}` : name;
};

const resolveFieldToIata = (display, keepCode) => {
  const fromText = resolveIataFromFreeText(display);
  if (fromText && /^[A-Z]{3}$/.test(fromText)) return fromText;
  const t = (display || '').trim();
  if (/^[A-Za-z]{3}$/.test(t)) return t.toUpperCase();
  const k = (keepCode || '').trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(k)) return k;
  return '';
};

const defaultDepart = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
};

const defaultReturn = () => {
  const d = new Date();
  d.setDate(d.getDate() + 21);
  return d.toISOString().split('T')[0];
};

const formatFlightTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const sliceLabel = (sliceCount, index) => {
  if (sliceCount <= 1) return null;
  return index === 0 ? 'Outbound' : index === 1 ? 'Return' : `Flight ${index + 1}`;
};

const Flights = ({ onGoHome, onNavigate }) => {
  const navigate = useNavigate();
  const { formatFrom } = useCurrency();
  const [tripType, setTripType] = useState('return');
  const defaultOriginCode = 'LON';
  const defaultDestCode = 'NYC';
  const [form, setForm] = useState({
    origin: defaultOriginCode,
    destination: defaultDestCode,
    departDate: defaultDepart(),
    returnDate: defaultReturn(),
    adults: 1,
    cabin_class: 'economy',
    max_connections: 1,
  });
  const [originDisplay, setOriginDisplay] = useState(
    () => defaultDisplayForCode(defaultOriginCode) || defaultOriginCode
  );
  const [destDisplay, setDestDisplay] = useState(
    () => defaultDisplayForCode(defaultDestCode) || defaultDestCode
  );
  const [catalogCities, setCatalogCities] = useState(() => {
    const sync = getSearchCatalogSync();
    return sync?.cities?.length ? sync.cities : [];
  });
  const [fieldMenu, setFieldMenu] = useState(null);
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [showOriginDd, setShowOriginDd] = useState(false);
  const [showDestDd, setShowDestDd] = useState(false);
  const placesWrapRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offers, setOffers] = useState([]);

  const flightCityIndex = useMemo(
    () => buildLocationSearchIndex(catalogCities, []),
    [catalogCities]
  );

  useEffect(() => {
    if (catalogCities.length > 0) return;
    let cancelled = false;
    fetchSearchCatalogFromNetwork().then((res) => {
      if (cancelled || !Array.isArray(res?.cities) || res.cities.length === 0) return;
      setCatalogCities(res.cities);
    });
    return () => {
      cancelled = true;
    };
  }, [catalogCities.length]);

  useEffect(() => {
    const handleDown = (event) => {
      if (placesWrapRef.current && !placesWrapRef.current.contains(event.target)) {
        setShowOriginDd(false);
        setShowDestDd(false);
        setFieldMenu(null);
      }
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, []);

  useEffect(() => {
    if (!fieldMenu || !flightCityIndex) return;
    const text = fieldMenu === 'origin' ? originDisplay : destDisplay;
    const q = text.trim();
    if (q.length < MIN_FLIGHT_PLACE_QUERY) {
      if (fieldMenu === 'origin') {
        setOriginSuggestions([]);
        setShowOriginDd(false);
      } else {
        setDestSuggestions([]);
        setShowDestDd(false);
      }
      return;
    }
    const handle = window.setTimeout(() => {
      const list = searchLocationIndex(flightCityIndex, q, {
        limit: 14,
        minLength: MIN_FLIGHT_PLACE_QUERY,
      });
      const cities = list.filter((s) => s.type === 'city');
      if (fieldMenu === 'origin') {
        setOriginSuggestions(cities);
        setShowOriginDd(cities.length > 0);
      } else {
        setDestSuggestions(cities);
        setShowDestDd(cities.length > 0);
      }
    }, 130);
    return () => window.clearTimeout(handle);
  }, [fieldMenu, originDisplay, destDisplay, flightCityIndex]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyOriginSuggestion = (sug) => {
    const label = buildFlightCityDisplayLabel(sug);
    const code =
      resolveIataFromCatalogCity(sug) || resolveIataFromFreeText(label);
    setOriginDisplay(label);
    if (code) updateField('origin', code);
    setShowOriginDd(false);
    setFieldMenu(null);
  };

  const applyDestSuggestion = (sug) => {
    const label = buildFlightCityDisplayLabel(sug);
    const code =
      resolveIataFromCatalogCity(sug) || resolveIataFromFreeText(label);
    setDestDisplay(label);
    if (code) updateField('destination', code);
    setShowDestDd(false);
    setFieldMenu(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOffers([]);

    const oResolved = resolveFieldToIata(originDisplay, form.origin);
    const dResolved = resolveFieldToIata(destDisplay, form.destination);
    if (!oResolved || !dResolved) {
      setLoading(false);
      setError(
        'Enter a valid departure and arrival: pick a city from the suggestions (same catalog as hotels) or type a 3-letter airport/city code (e.g. LON, JFK).'
      );
      return;
    }
    if (oResolved !== form.origin) updateField('origin', oResolved);
    if (dResolved !== form.destination) updateField('destination', dResolved);

    try {
      const passengers = buildFlightPassengers(form.adults, []);
      const sliceOrigin = oResolved;
      const sliceDest = dResolved;
      const dep = form.departDate;
      const ret = form.returnDate;
      const computedSlices =
        !sliceOrigin || !sliceDest || !dep
          ? []
          : tripType === 'return'
            ? ret
              ? [
                  { origin: sliceOrigin, destination: sliceDest, departure_date: dep },
                  { origin: sliceDest, destination: sliceOrigin, departure_date: ret },
                ]
              : [{ origin: sliceOrigin, destination: sliceDest, departure_date: dep }]
            : [{ origin: sliceOrigin, destination: sliceDest, departure_date: dep }];

      if (!computedSlices.length) {
        setLoading(false);
        const msg = !dep
          ? 'Please choose a departure date.'
          : tripType === 'return' && !ret
            ? 'Round-trip searches need a return date — pick one or switch to one-way.'
            : 'Could not build flight routes — check airports and dates.';
        setError(msg);
        return;
      }

      const payload = {
        slices: computedSlices,
        passengers,
        cabin_class: form.cabin_class || 'economy',
        max_connections: Number(form.max_connections) || 1,
        return_offers: true,
        supplier_timeout: 45000,
      };

      const res = await createFlightOfferRequest(payload);
      const list = Array.isArray(res?.data?.offers) ? res.data.offers : [];
      setOffers(list);
      if (list.length === 0) {
        setError(
          'No flights returned. Try different dates or nearby airports — or enter explicit 3-letter codes (e.g. LHR, JFK).'
        );
      }
    } catch (err) {
      setError(err?.message || 'Flight search failed');
    } finally {
      setLoading(false);
    }
  };

  const goToOfferDetail = (offerId) => {
    navigate(`/flights/offer/${encodeURIComponent(offerId)}`, {
      state: { offerId },
    });
  };

  return (
    <div className="flights-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="flights-main">
        <div className="flights-container">
          <section className="flights-hero">
            <h1 className="flights-hero__title">Search flights</h1>
            <p className="flights-hero__subtitle">
              Powered by Duffel. Search by city name like on hotels — pick from the list — or enter a 3-letter
              code (e.g. LHR, JFK).
            </p>
          </section>

          <div className="flights-search-card">
          <form
            className={`flights-form${tripType === 'return' ? ' flights-form--return-trip' : ''}`}
            onSubmit={handleSubmit}
          >
            <div className="flights-trip-toggle">
              <button
                type="button"
                className={`flights-trip-toggle__btn ${tripType === 'one_way' ? 'flights-trip-toggle__btn--active' : ''}`}
                onClick={() => setTripType('one_way')}
              >
                One-way
              </button>
              <button
                type="button"
                className={`flights-trip-toggle__btn ${tripType === 'return' ? 'flights-trip-toggle__btn--active' : ''}`}
                onClick={() => setTripType('return')}
              >
                Return
              </button>
            </div>

            <div className="flights-place-group" ref={placesWrapRef}>
              <label className="flights-label flights-label--place">
                From
                <div className="flights-place">
                  <input
                    className="flights-input"
                    id="flights-origin"
                    autoComplete="off"
                    value={originDisplay}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOriginDisplay(v);
                      setFieldMenu('origin');
                    }}
                    onFocus={() => setFieldMenu('origin')}
                    onBlur={() => {
                      window.setTimeout(() => {
                        const code = resolveFieldToIata(originDisplay, form.origin);
                        if (code) updateField('origin', code);
                      }, 180);
                    }}
                    placeholder="City or airport code"
                    required
                    aria-autocomplete="list"
                    aria-expanded={showOriginDd}
                    aria-controls="flights-origin-listbox"
                  />
                  {showOriginDd && originSuggestions.length > 0 ? (
                    <ul
                      id="flights-origin-listbox"
                      className="flights-place__dropdown"
                      role="listbox"
                    >
                      {originSuggestions.map((sug) => (
                        <li key={`o-${sug.id}-${sug.name}-${sug.country || ''}`} role="option">
                          <button
                            type="button"
                            className="flights-place__option"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => applyOriginSuggestion(sug)}
                          >
                            <span className="flights-place__option-name">{sug.name}</span>
                            {sug.country ? (
                              <span className="flights-place__option-meta">{sug.country}</span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </label>
              <label className="flights-label flights-label--place">
                To
                <div className="flights-place">
                  <input
                    className="flights-input"
                    id="flights-destination"
                    autoComplete="off"
                    value={destDisplay}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDestDisplay(v);
                      setFieldMenu('dest');
                    }}
                    onFocus={() => setFieldMenu('dest')}
                    onBlur={() => {
                      window.setTimeout(() => {
                        const code = resolveFieldToIata(destDisplay, form.destination);
                        if (code) updateField('destination', code);
                      }, 180);
                    }}
                    placeholder="City or airport code"
                    required
                    aria-autocomplete="list"
                    aria-expanded={showDestDd}
                    aria-controls="flights-destination-listbox"
                  />
                  {showDestDd && destSuggestions.length > 0 ? (
                    <ul
                      id="flights-destination-listbox"
                      className="flights-place__dropdown"
                      role="listbox"
                    >
                      {destSuggestions.map((sug) => (
                        <li key={`d-${sug.id}-${sug.name}-${sug.country || ''}`} role="option">
                          <button
                            type="button"
                            className="flights-place__option"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => applyDestSuggestion(sug)}
                          >
                            <span className="flights-place__option-name">{sug.name}</span>
                            {sug.country ? (
                              <span className="flights-place__option-meta">{sug.country}</span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </label>
            </div>
            <label className="flights-label flights-label--place">
              Depart
              <input
                className="flights-input"
                type="date"
                value={form.departDate}
                onChange={(e) => updateField('departDate', e.target.value)}
                required
              />
            </label>
            {tripType === 'return' ? (
              <label className="flights-label">
                Return
                <input
                  className="flights-input"
                  type="date"
                  value={form.returnDate}
                  onChange={(e) => updateField('returnDate', e.target.value)}
                  required
                />
              </label>
            ) : null}
            <label className="flights-label">
              Adults
              <input
                className="flights-input"
                type="number"
                min="1"
                value={form.adults}
                onChange={(e) => updateField('adults', e.target.value)}
                required
              />
            </label>
            <label className="flights-label">
              Cabin
              <select
                className="flights-input"
                value={form.cabin_class}
                onChange={(e) => updateField('cabin_class', e.target.value)}
              >
                <option value="economy">Economy</option>
                <option value="premium_economy">Premium economy</option>
                <option value="business">Business</option>
                <option value="first">First</option>
              </select>
            </label>
            <label className="flights-label flights-label--max-stops">
              Max stops / slice
              <input
                className="flights-input"
                type="number"
                min="0"
                max="3"
                value={form.max_connections}
                onChange={(e) => updateField('max_connections', e.target.value)}
              />
            </label>

            <div className="flights-submit-wrap">
              <Button type="submit" variant="primary" size="lg" disabled={loading}>
                {loading ? 'Searching…' : 'Search flights'}
              </Button>
            </div>
          </form>
          </div>

          {error ? <div className="flights-error">{error}</div> : null}

          <section className="flights-results" aria-live="polite">
            <h2 className="flights-results__heading">Results</h2>
            {!offers.length && !loading && !error ? (
              <p className="flights-results__empty">Run a search to see offers.</p>
            ) : null}
            <ul className="flights-offer-list">
              {offers.map((offer) => {
                const sum = summarizeFlightOffer(offer);
                const sliceCount = sum?.slices?.length ?? 0;
                const hasPrice = sum?.total_amount != null && sum?.total_currency;
                return (
                  <li key={offer.id} className="flights-offer-card">
                    <div
                      className="flights-offer-card__shell"
                      role="button"
                      tabIndex={0}
                      onClick={() => goToOfferDetail(offer.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToOfferDetail(offer.id);
                        }
                      }}
                    >
                      <div className="flights-offer-card__glow" aria-hidden />
                      <div className="flights-offer-card__body">
                        <div className="flights-offer-card__routes">
                          {sum?.slices?.map((row, idx) => {
                            const label = sliceLabel(sliceCount, idx);
                            const depT = formatFlightTime(row.dep);
                            const arrT = formatFlightTime(row.arr);
                            const direct = row.stops === 0;
                            return (
                              <div key={`${offer.id}-s-${idx}`} className="flights-offer-card__segment">
                                {label ? (
                                  <span className="flights-offer-card__segment-title">{label}</span>
                                ) : null}
                                <div className="flights-offer-card__segment-main">
                                  <div className="flights-offer-card__endpoint">
                                    <span className="flights-offer-card__code">{row.origin}</span>
                                    {depT ? (
                                      <span className="flights-offer-card__time">{depT}</span>
                                    ) : null}
                                  </div>
                                  <div className="flights-offer-card__connector-wrap" aria-hidden>
                                    <span className="flights-offer-card__connector-line" />
                                    <span className="flights-offer-card__connector-icon">✈</span>
                                    <span className="flights-offer-card__connector-line" />
                                  </div>
                                  <div className="flights-offer-card__endpoint flights-offer-card__endpoint--dest">
                                    <span className="flights-offer-card__code">{row.dest}</span>
                                    {arrT ? (
                                      <span className="flights-offer-card__time">{arrT}</span>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="flights-offer-card__segment-foot">
                                  <span
                                    className={
                                      direct
                                        ? 'flights-offer-card__pill flights-offer-card__pill--direct'
                                        : 'flights-offer-card__pill flights-offer-card__pill--stops'
                                    }
                                  >
                                    {direct ? 'Direct' : `${row.stops} stop${row.stops === 1 ? '' : 's'}`}
                                  </span>
                                  {row.marketing ? (
                                    <span className="flights-offer-card__airline">{row.marketing}</span>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flights-offer-card__aside">
                          <div className="flights-offer-card__price-block">
                            <span className="flights-offer-card__price-label">Total price</span>
                            {hasPrice ? (
                              <span className="flights-offer-card__price-row">
                                <span className="flights-offer-card__price-formatted">
                                  {formatFrom(sum.total_amount, sum.total_currency)}
                                </span>
                              </span>
                            ) : (
                              <span className="flights-offer-card__amount-placeholder">View fare</span>
                            )}
                            <span className="flights-offer-card__price-note">Taxes & fees included</span>
                          </div>
                          <Button
                            type="button"
                            variant="primary"
                            size="md"
                            className="flights-offer-card__cta"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToOfferDetail(offer.id);
                            }}
                          >
                            View details
                          </Button>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Flights;
